import { useField, useFormikContext } from 'formik'
import { useEffect, useMemo } from 'react'
import type { ActivityForm } from '../typings'
import { add } from 'date-fns'
import { DEFAULT_ACTIVITY_MINUTES } from '../../../constants'

export function useStartDateManagement() {
  const [{ value }] = useField('startTime')
  const {
    values: { startDate, dueDate, dueTime },
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ActivityForm>()

  const dueTimDefault = useMemo(
    () =>
      add(new Date(), {
        days: 1,
      }).setHours(8, 0),
    [],
  )

  const combineDateAndTime = (date: Date, time: Date | null): Date | null => {
    if (!date) return null
    if (!time) return new Date(date)

    const timeVal = new Date(time)

    const hours = timeVal.getHours()
    const minutes = timeVal.getMinutes()
    const combined = new Date(date)
    combined.setHours(hours, minutes, 0, 0)
    return combined
  }

  useEffect(() => {
    if (value) {
      setFieldTouched('startDate', true)
    }

    const startDateTime = combineDateAndTime(startDate, value)
    const dueDateTime = combineDateAndTime(dueDate, dueTime)

    if (startDate) {
      if (startDateTime && (!dueDateTime || startDateTime >= dueDateTime)) {
        setFieldValue('dueDate', startDate)
        // SH-2133: 30 minutes, not an hour. A calendar click seeds a 30-minute
        // block; repairing with +1h silently widened it to an hour the first
        // time the user edited Start. Changed globally rather than behind a
        // prop so all three hosts of this hook (calendar modal, entity-scoped
        // Activity panel, Edit Activity popover) agree on one interval.
        setFieldValue(
          'dueTime',
          add(value ?? dueTimDefault, {
            minutes: DEFAULT_ACTIVITY_MINUTES,
          }).getTime(),
        )
      }

      if (!value) {
        setFieldValue('startTime', dueTimDefault)
        setFieldTouched('startTime', true)
      }
    } else {
      setFieldValue('startTime', null)
    }
  }, [
    value,
    setFieldTouched,
    startDate,
    setFieldValue,
    setFieldTouched,
    dueTimDefault,
  ])

  return {
    timeDisabled: useMemo(() => !startDate, [startDate]),
  }
}
